import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import AppNotify from '../AppNotify/AppNotify';
import UserMenu from '../UserMenu/UserMenu';
import Sidebar from '../Sidebar/Sidebar';
import FeedbackForm from '../FeedbackForm/FeedbackForm';
import WarningPopup from '../Popup/Warning/Warning';

import { getCustomState } from '../../../util/LocalStorage';
import {
  deleteFeedback,
  deletePendingFeedback,
  getAllFeedback,
  getCouncillorFeedback,
  getPendingFeedback,
} from '../../../http/feedbackAPI';
import { setFeedback, setFeedbackLoading, setPendingFeedback } from '../../../datastore/actions/dataActions';
import { SESSION_STATE, USER_DATA, USER_ROLES } from '../../../util/constants/AppConstants';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { setNotify } from '../../../datastore/actions/actionActions';

import UserIcon from '../../../assets/images/icons/user.svg';
import './Header.scss';

const Header = () => {
  const [dataFetching, setDataFetching] = useState(false);
  const [userDataState, setUserDataState] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isUserMenuOpened, setIsUserMenuOpened] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [activeMessage, setActiveMessage] = useState(null);

  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);
  const actions = useSelector((state) => state.action);
  const sessionRedux = useSelector((state) => state.session.session);
  const session = getCustomState(SESSION_STATE);

  const profileRef = useRef();
  const profileIcon = useRef();
  const menuCloseIcon = useRef();
  const menuRef = useRef();

  const { pathname } = useLocation();
  const isMessage = pathname.startsWith('/email/');
  const isSettings = pathname === '/settings';
  const isPendingMessages = pathname === '/pending_emails';
  const isActivityMap = pathname === '/activity_map';
  const isConstituents = pathname === '/constituents';
  const isConstituentPage = pathname.startsWith('/constituent/');
  const isCases = pathname === '/cases';
  const isCasePage = pathname.startsWith('/case/');
  const isFilesPage = pathname.startsWith('/files');

  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const navigate = useNavigate();

  const lastRequestId = useRef(0);

  const handleGetFeedbackData = async (userId, role) => {
    const requestId = ++lastRequestId.current;

    dispatch(setFeedbackLoading(true));

    const isCouncillor = role === councilorRoleValue;

    const res = isCouncillor ? await getCouncillorFeedback(config, userId) : await getAllFeedback(config, userId);

    if (requestId !== lastRequestId.current) return;

    if (res.status === 200) {
      dispatch(setFeedback(res.data.feedback));
      dispatch(setFeedbackLoading(false));
    } else {
      console.log('ERROR: Grabbing Feedback');
    }
  };

  const handleGetPendingFeedbackData = async (userId) => {
    const res = await getPendingFeedback(config, userId);

    if (res.status === 200) {
      dispatch(setPendingFeedback(res.data.feedback));
      return res.data.feedback;
    }

    console.log('ERROR: Grabbing Pending Feedback');
  };

  const handleGetFirstPendingFeedbackData = async (userId) => {
    const pendingFeedback = await handleGetPendingFeedbackData(userId);

    if (pendingFeedback.length > 0) {
      dispatch(
        setNotify({
          status: true,
          type: '',
          title: 'Attention',
          message: 'You have unconfirmed email messages. To manage them, go to the "Pending Emails" tab',
        }),
      );
    }
  };

  useEffect(() => {
    if (config.api && session && !dataFetching) {
      const userData = getCustomState(USER_DATA, true);
      setUserInfo(userData);
      setDataFetching(true);

      handleGetFeedbackData(userData.id, userData.role);
      handleGetFirstPendingFeedbackData(userData.id);
    }
  }, [config, session, userDataState, dataFetching]);

  useEffect(() => {
    setUserDataState(getCustomState(USER_DATA, true));
  }, [sessionRedux]);

  useEffect(() => {
    if (!userInfo && userDataState && config.api && !actions.loggingIn) {
      setUserInfo(userDataState);
    }
  }, [userDataState, userInfo, config, actions]);

  const pageTitles = {
    isMessage: 'Email Details',
    isSettings: 'Settings',
    isPendingMessages: 'Pending Emails',
    isActivityMap: 'Activity Map',
    isConstituents: 'Constituents',
    isConstituentPage: 'Constituent Data',
    isCases: 'Cases',
    isCasePage: 'Case Details',
    isFilesPage: 'Files',
  };

  const getPageTitle = () => {
    if (isMessage) return pageTitles.isMessage;
    if (isSettings) return pageTitles.isSettings;
    if (isPendingMessages) return pageTitles.isPendingMessages;
    if (isActivityMap) return pageTitles.isActivityMap;
    if (isConstituents) return pageTitles.isConstituents;
    if (isConstituentPage) return pageTitles.isConstituentPage;
    if (isCases) return pageTitles.isCases;
    if (isCasePage) return pageTitles.isCasePage;
    if (isFilesPage) return pageTitles.isFilesPage;
    return 'Emails';
  };

  const handleOpenUserMenu = () => {
    setIsUserMenuOpened((prevState) => !prevState);
  };

  const toggleMobileMenu = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  const handleCloseForm = () => {
    setIsModalOpen(false);
    setActiveMessage(null);
  };

  const handleMessageDelete = async () => {
    const deleteFunction = isPendingMessages ? deletePendingFeedback : deleteFeedback;
    const res = await deleteFunction(config, deleteId, userInfo.id);

    if (res.status === 200) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }

    if (isMessage) {
      navigate('/emails');
    }
    setDeleteRequest(false);
    setDeleteId(null);
    if (isPendingMessages) {
      handleGetPendingFeedbackData(userInfo.id);
    } else {
      handleGetFeedbackData(userInfo.id, userInfo.role);
    }
  };

  useOutsideClick(profileRef, profileIcon, isUserMenuOpened, () => setIsUserMenuOpened(false));
  useOutsideClick(menuRef, menuCloseIcon, isSidebarOpen, () => setIsSidebarOpen(false));

  return (
    <>
      <AppNotify />
      <div className="content">
        <div ref={menuCloseIcon} className="mobile-menu">
          <span onClick={toggleMobileMenu} className={isSidebarOpen ? 'open-menu' : ''}>
            <div></div>
            <div></div>
            <div></div>
          </span>
        </div>
        <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} menuRef={menuRef} />
        <div className="content__wrap">
          <div className="navigation">
            <h2 className="navigation__title">{getPageTitle()}</h2>
            <div className="navigation__user">
              <div
                ref={profileIcon}
                className={`user-icon ${isUserMenuOpened ? 'opened' : ''}`}
                onClick={handleOpenUserMenu}
              >
                <img src={UserIcon} />
              </div>
            </div>
            {isUserMenuOpened && (
              <UserMenu profileRef={profileRef} setIsOpen={setIsUserMenuOpened} userInfo={userInfo} />
            )}
          </div>
          <div className="outlet-wrap">
            <Outlet
              context={{
                userInfo,
                setUserInfo,
                setIsModalOpen,
                handleGetFeedbackData,
                handleGetPendingFeedbackData,
                setActiveMessage,
                setDeleteRequest,
                setDeleteId,
              }}
            />
          </div>
        </div>
        {isModalOpen && (
          <FeedbackForm
            isOpen={isModalOpen}
            onCloseHandler={handleCloseForm}
            feedbackData={activeMessage}
            handleGetFeedbackData={() => handleGetFeedbackData(userInfo.id, userInfo.role)}
            userInfo={userInfo}
          />
        )}
        {deleteRequest && (
          <WarningPopup
            isOpen={deleteRequest}
            onSubmitHandler={handleMessageDelete}
            onCancelHandler={() => setDeleteRequest(false)}
          />
        )}
      </div>
    </>
  );
};

export default Header;
